/**
 * General Settings Page (Container with Tabs)
 * Optimized UI/UX for modern, minimalist, professional appearance
 * Designed for 1077x750 iframe window
 * Tab 1: General Settings - DCS App configuration
 * Tab 2: Kiosk Settings - Kiosk-specific configuration
 */

import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useGeneralSettingsStore } from '../../stores/general-settings-store';
import { useKioskSettingsStore } from '../../stores/kiosk-settings-store';
import { CreateDcsAppRequest, DcsApp } from '../../types/general-settings-types';
import { AppSection } from '../sections/AppSection';
import { PaymentSection } from '../sections/PaymentSection';
import { LinksSection } from '../sections/LinksSection';
import { SystemSection } from '../sections/SystemSection';
import { KioskSettingsTab } from './KioskSettingsTab';
import { validatePreAuth } from '../../utils/pre-auth-validation';
import { Button } from '@/core/components/ui/button';
import { useOperatorStore } from '@/core/stores/operator-store';
import { LanguageSwitcher } from '@/core/i18n/components/LanguageSwitcher';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

type SettingsTab = 'general' | 'kiosk';

export const GeneralSettingsPage = () => {
  const { operatorId: urlOperatorId } = useParams<{ operatorId: string }>();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const { t } = useTranslation('assignEv');
  const { t: tCommon } = useTranslation('common');

  // The backoffice passes the real operatorId as ?actorId= query param (the path contains a literal "{actorId}")
  // Fallback chain: query param actorId → URL path param → store → debug ID
  const queryActorId = parseInt(searchParams.get('actorId') || '', 10);
  const urlOperator = parseInt(urlOperatorId || '', 10);
  const storeOperator = useOperatorStore((state) => state.selectedOperator);
  const operatorId = !isNaN(queryActorId) ? queryActorId : (!isNaN(urlOperator) ? urlOperator : (storeOperator?.operatorId || 2002123712)); // Debug fallback

  // General Settings Store
  const {
    app,
    originalApp,
    isLoading,
    isSaving,
    error,
    fetchSettings,
    createSettings,
    updateSettings,
    resetSettings,
    clearError: clearGeneralError
  } = useGeneralSettingsStore();

  // Kiosk Settings Store
  const {
    isSaving: kioskIsSaving,
    error: kioskError,
    saveSettings: kioskSaveSettings,
    resetSettings: kioskResetSettings,
    hasChanges: kioskHasChanges,
    clearError: clearKioskError,
    settings: kioskSettings
  } = useKioskSettingsStore();

  const kioskMaxFeeValue = kioskSettings?.maxTransactionFeePercentage ?? null;
  const kioskAcValue = kioskSettings?.acCreditBuffer ?? null;
  const kioskDcValue = kioskSettings?.dcCreditBuffer ?? null;
  const kioskMinCostValue = kioskSettings?.minCostPerMinuteThreshold ?? null;
  const kioskKwhCostValue = kioskSettings?.averageKwhCost ?? null;
  const hasKioskValidationError =
    (kioskMaxFeeValue !== null && (kioskMaxFeeValue < 0 || kioskMaxFeeValue > 100)) ||
    (kioskAcValue !== null && (kioskAcValue < 0 || kioskAcValue > 99.9)) ||
    (kioskDcValue !== null && (kioskDcValue < 0 || kioskDcValue > 99.9)) ||
    (kioskMinCostValue !== null && kioskMinCostValue <= 0) ||
    (kioskKwhCostValue !== null && (kioskKwhCostValue < 0.1 || kioskKwhCostValue > 99999)) ||
    // Operator level: no Default Credit ceiling
    validatePreAuth(kioskSettings?.preAuth).hasError;

  // true when the operator has no DCS app yet (create mode vs edit mode)
  const isCreateMode = !isLoading && app === null;

  const [localApp, setLocalApp] = useState<DcsApp | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const saveSuccessTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Accordion state - only one section open at a time
  const [openSection, setOpenSection] = useState<string>('app'); // First section (App) open by default

  // Auto-dismiss errors after 10 seconds
  useEffect(() => {
    if (error) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => clearGeneralError(), 10000);
    }
  }, [error]);

  useEffect(() => {
    if (kioskError) {
      if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = setTimeout(() => clearKioskError(), 10000);
    }
  }, [kioskError]);

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings(operatorId);
  }, [fetchSettings]);

  // Sync local state with store — also seed empty form in create mode
  useEffect(() => {
    if (app) {
      setLocalApp(app);
    } else if (isCreateMode) {
      // Seed an empty form so the user can fill in the fields
      setLocalApp({
        appId: 0,
        operatorId,
        name: '',
        slug: '',
        billingCycleDay: 1,
        minEngineBillingAmount: 0.01,
        minEnergyForConnectionFee: 1.0,
        showPriceOnSession: false,
        incrementalAuthType: 'None',
        terminalRequiredTz: false,
      } as DcsApp);
    }
  }, [app, isCreateMode]);

  const handleFieldChange = (field: string, value: any) => {
    if (localApp) {
      setLocalApp({
        ...localApp,
        [field]: value
      });
    }
  };

  const showSaveSuccess = () => {
    setSaveSuccess(true);
    if (saveSuccessTimerRef.current) clearTimeout(saveSuccessTimerRef.current);
    saveSuccessTimerRef.current = setTimeout(() => setSaveSuccess(false), 5000);
  };

  const handleSave = async () => {
    setSaveSuccess(false);
    if (activeTab === 'general') {
      if (!localApp) return;
      try {
        if (isCreateMode) {
          // Build create request from form values
          const incrementalAuthMap: Record<string, number> = { None: 0, SingleDuringSettlement: 1, Multiple: 2 };
          const request: CreateDcsAppRequest = {
            operatorId,
            merchantId: 1,
            slug: localApp.slug,
            terminalRequiredTz: localApp.terminalRequiredTz ?? false,
            billingCycleDay: localApp.billingCycleDay ?? 1,
            incrementalAuthType: incrementalAuthMap[localApp.incrementalAuthType ?? 'None'] ?? 0,
            name: localApp.name || undefined,
            logoUrl: localApp.logoUrl || undefined,
            smsKey: localApp.smsKey || undefined,
            currency: localApp.currency || undefined,
            showPriceOnSession: localApp.showPriceOnSession,
            noBilling: localApp.noBilling,
            useThreeDs: localApp.useThreeDs,
            supportEcomSDK: localApp.supportEcomSDK,
            billingBarrierDays: localApp.billingBarrierDays,
            minEngineBillingAmount: localApp.minEngineBillingAmount,
            minEnergyForConnectionFee: localApp.minEnergyForConnectionFee,
            autoStopDefaultCreditDeltaAmount: localApp.autoStopDefaultCreditDeltaAmount,
            standingOrderTerminal: localApp.standingOrderTerminal,
            singleChargeTerminal: localApp.singleChargeTerminal,
            validationTerminal: localApp.validationTerminal,
            eReceiptAppId: localApp.eReceiptAppId,
            countryCode: localApp.countryCode || undefined,
            termsUrl: localApp.termsUrl || undefined,
            privacyUrl: localApp.privacyUrl || undefined,
            accessibilityUrl: localApp.accessibilityUrl || undefined,
            contactUsUrl: localApp.contactUsUrl || undefined,
            contactUsNumber: localApp.contactUsNumber || undefined,
            aboutUrl: localApp.aboutUrl || undefined,
            website: localApp.website || undefined,
            qrRedirectUrlTemplate: localApp.qrRedirectUrlTemplate || undefined,
            defaultTimeZone: localApp.defaultTimeZone,
          };
          await createSettings(request);
          showSaveSuccess();
        } else {
          await updateSettings(localApp);
          showSaveSuccess();
        }
      } catch (error) {
        console.error('Failed to save settings:', error);
      }
    } else if (activeTab === 'kiosk') {
      try {
        await kioskSaveSettings();
        toast.success(t('kiosk.saveSuccess'));
      } catch (error) {
        console.error('Failed to save kiosk settings:', error);
      }
    }
  };

  const handleReset = () => {
    if (activeTab === 'general') {
      // General Settings Tab
      resetSettings();
      if (originalApp) {
        setLocalApp(JSON.parse(JSON.stringify(originalApp)));
      }
    } else if (activeTab === 'kiosk') {
      // Kiosk Settings Tab
      kioskResetSettings();
    }
  };

  // Handle section toggle (accordion behavior)
  const handleSectionToggle = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? '' : sectionId);
  };

  // In create mode: enabled once slug is filled. In edit mode: compare with original.
  const computeHasChanges = () => {
    if (isCreateMode) return !!(localApp?.slug?.trim());
    if (!localApp || !originalApp) return false;
    return JSON.stringify(localApp) !== JSON.stringify(originalApp);
  };

  const hasLocalChanges = computeHasChanges();

  // Detect changes in specific sections
  const hasAppChanges = () => {
    if (!localApp || !originalApp) return false;
    const fields = ['name', 'slug', 'logoUrl', 'smsKey', 'showPriceOnSession'];
    return fields.some(field =>
      JSON.stringify((localApp as any)[field]) !== JSON.stringify((originalApp as any)[field])
    );
  };

  const hasPaymentChanges = () => {
    if (!localApp || !originalApp) return false;
    const fields = ['noBilling', 'supportEcomSDK', 'useThreeDs', 'billingCycleDay', 'billingBarrierDays',
                    'validationTerminal', 'singleChargeTerminal', 'incrementalAuthType', 'minEnergyForConnectionFee',
                    'currency', 'minEngineBillingAmount', 'autoStopDefaultCreditDeltaAmount', 'eReceiptAppId'];
    return fields.some(field =>
      JSON.stringify((localApp as any)[field]) !== JSON.stringify((originalApp as any)[field])
    );
  };

  const hasLinksChanges = () => {
    if (!localApp || !originalApp) return false;
    const fields = ['contactUsNumber', 'contactUsUrl', 'termsUrl', 'privacyUrl', 'website', 'aboutUrl', 'accessibilityUrl', 'qrRedirectUrlTemplate'];
    return fields.some(field =>
      JSON.stringify((localApp as any)[field]) !== JSON.stringify((originalApp as any)[field])
    );
  };

  const hasSystemChanges = () => {
    if (!localApp || !originalApp) return false;
    const fields = ['defaultTimeZone', 'countryCode'];
    return fields.some(field =>
      JSON.stringify((localApp as any)[field]) !== JSON.stringify((originalApp as any)[field])
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="inline-flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-500 mx-auto mb-4" />
          </div>
          <p className="text-gray-700 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error dead-end only when there's an actual API error (not just "no app found")
  if (!isCreateMode && !localApp && error) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  // localApp is seeded by useEffect — wait one cycle before rendering form
  if (!localApp) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-gray-500 mx-auto mb-4" />
          <p className="text-gray-700 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const currentHasChanges = activeTab === 'general' ? hasLocalChanges : kioskHasChanges();
  const currentIsSaving = activeTab === 'general' ? isSaving : kioskIsSaving;
  const currentError = activeTab === 'general' ? error : kioskError;

  return (
    <div className="flex flex-col h-screen bg-white">
      {/* Tab Navigation with Language Switcher */}
      <div className="border-b-2 border-gray-200 bg-white px-6 shadow-sm flex items-center justify-between">
        <div className="flex gap-8">
          <button
            onClick={() => setActiveTab('general')}
            className={`py-4 px-2 text-base transition-all border-b-4 relative ${
              activeTab === 'general'
                ? 'border-yellow-400 text-gray-900 font-semibold'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('tabs.general')}
            {/* {hasAppChanges() && (
              <span className="absolute right-0 top-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            )} */}
          </button>
          <button
            onClick={() => !isCreateMode && setActiveTab('kiosk')}
            disabled={isCreateMode}
            title={isCreateMode ? 'Save the operator first to enable Kiosk Settings' : undefined}
            className={`py-4 px-2 text-base transition-all border-b-4 relative ${
              isCreateMode
                ? 'border-transparent text-gray-300 cursor-not-allowed'
                : activeTab === 'kiosk'
                  ? 'border-yellow-400 text-gray-900 font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('tabs.kiosk')}
            {/* {kioskHasChanges() && (
              <span className="absolute right-0 top-1 w-2 h-2 bg-orange-500 rounded-full animate-pulse" />
            )} */}
          </button>
        </div>
        <LanguageSwitcher />
      </div>

      {/* Floating bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 z-50 flex items-end justify-between px-4 py-3 pointer-events-none">
          <div className="pointer-events-auto">
            {currentError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 shadow-md opacity-90">
                <AlertCircle className="h-4 w-4 shrink-0" />
                <span>{currentError}</span>
              </div>
            )}
            {saveSuccess && !currentError && (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 shadow-md opacity-90">
                <CheckCircle2 className="h-4 w-4 shrink-0" />
                <span>{tCommon('messages.saveSuccess')}</span>
              </div>
            )}
          </div>
          <div className="pointer-events-auto flex items-center gap-3">
            {(activeTab === 'kiosk' && hasKioskValidationError) && (
              <span className="text-xs text-red-600 font-medium">Fix validation errors to save</span>
            )}
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!currentHasChanges || currentIsSaving}
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {tCommon('buttons.reset')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={!currentHasChanges || currentIsSaving || (activeTab === 'kiosk' && hasKioskValidationError)}
              className="flex items-center gap-2 rounded-lg bg-yellow-400 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-lg hover:bg-yellow-500 disabled:opacity-50"
            >
              {currentIsSaving ? tCommon('status.saving') : (activeTab === 'general' && isCreateMode ? 'Create' : tCommon('buttons.save'))}
            </Button>
          </div>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-hidden bg-gray-100 flex flex-col">
        {/* Kiosk Settings Tab - no padding, full size */}
        {activeTab === 'kiosk' && (
          <div className="flex-1 px-4 py-2 overflow-y-auto">
            <KioskSettingsTab operatorId={operatorId} />
          </div>
        )}

        <div className={`p-6 pb-20 overflow-y-auto flex-1 ${activeTab === 'kiosk' ? 'hidden' : ''}`}>
          {/* General Settings Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6 max-w-6xl">
              {isCreateMode && (
                <div className="flex items-center gap-2 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm font-medium text-yellow-800">
                  <AlertCircle className="h-4 w-4 shrink-0 text-yellow-500" />
                  This operator is not yet registered as an EV operator. Fill in the required fields (name, slug) and click <strong className="ml-1">Create</strong>.
                </div>
              )}
              <AppSection
                app={localApp}
                onFieldChange={handleFieldChange}
                hasChanges={hasAppChanges()}
                isOpen={openSection === 'app'}
                onToggle={() => handleSectionToggle('app')}
                isCreateMode={isCreateMode}
              />
              <PaymentSection
                app={localApp}
                onFieldChange={handleFieldChange}
                hasChanges={hasPaymentChanges()}
                isOpen={openSection === 'payment'}
                onToggle={() => handleSectionToggle('payment')}
              />
              <LinksSection
                app={localApp}
                onFieldChange={handleFieldChange}
                hasChanges={hasLinksChanges()}
                isOpen={openSection === 'links'}
                onToggle={() => handleSectionToggle('links')}
              />
              <SystemSection
                app={localApp}
                onFieldChange={handleFieldChange}
                hasChanges={hasSystemChanges()}
                isOpen={openSection === 'system'}
                onToggle={() => handleSectionToggle('system')}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
